import React, { useState, useEffect, useCallback, useMemo, SyntheticEvent, useRef } from "react";
import { Button, Dimmer, Icon, Loader, Pagination } from "semantic-ui-react";
import { useIntl } from "react-intl";

// UX
import CardSettingServiceUX from "react-lib/apps/MEH/CardSettingServiceUX";

// Components
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import SnackMessage from "../common/SnackMessage";
import ModSettingRule from "./ModSettingRule";

// Serializer
import ProductServiceSerializer from "issara-sdk/types/ProductServiceSerializer_core";

// Utils
import {
  ACTIONS,
  BTN_ACTS,
  PickedState,
  RunSequence,
  SaveServiceOrderType,
  SetProp,
  OnEvent,
  DEFAULT_PRODUCT_LIST_LIMIT,
  MAIN_CARD_NAME,
  serviceStatusOption,
  ProductListParams,
  ProductServiceParams,
} from "react-lib/apps/MEH/sequence/SaveServiceOrder";
import { formatADtoBEString } from "react-lib/utils/dateUtils";
import { formatComma } from "react-lib/utils/tsUtils";

type CardSettingServiceProps = {
  setProp: SetProp;
  runSequence: RunSequence;
  onEvent: OnEvent;
  controller?: any;

  // sequence
  SaveServiceOrderSequence?: SaveServiceOrderType;
  // CommonInterface
  masterData: any[];
  languageUX: "en" | "th";
} & Pick<
  PickedState,
  | "loadingStatus"
  | "buttonLoadCheck"
  | "errorMessage"
  | "successMessage"
  | "selectedDivision"
  | "userTokenize"
  | "masterOptions"
>;

const CardSettingService = (props: CardSettingServiceProps) => {
  const intl = useIntl();

  const [filter, setFilter] = useState<ProductServiceParams>({
    active_flag: "ALL",
  });
  const [openRuleMod, setOpenRuleMod] = useState<boolean>(false);
  const [selectService, setSelectService] = useState<Partial<ProductServiceSerializer>>({});

  // Use Effect
  useEffect(() => {
    props.runSequence({
      sequence: "SaveServiceOrder",
      action: ACTIONS.SEARCH_SERVICE,
      restart: true,
    });
  }, [props.selectedDivision]);

  const formattedServiceList = useMemo(() => {
    return (props.SaveServiceOrderSequence?.serviceList || []).map((item) => {
      return {
        ...item,
        serviceCode: <div style={{ display: "flex", justifyContent: "center" }}>{item.code}</div>,
        productType: <div>{item.p_type?.name || ""}</div>,
        servicePrice: (
          <div style={{ display: "flex", justifyContent: "flex-end", marginRight: "10px" }}>
            {formatComma(item.price)}
          </div>
        ),
        startDate: (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {formatADtoBEString(item.start_date)}
          </div>
        ),
        endDate: (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {formatADtoBEString(item.end_date)}
          </div>
        ),
        serviceStatus: (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              borderRadius: "10px",
              ...statusStyles[item.active_flag as "INACTIVE" | "ACTIVE"],
            }}
          >
            {item.active_flag_label}
          </div>
        ),
        action: (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              key={item.id}
              size="small"
              onClick={() => {
                handleSelectService(true, item);
              }}
            >
              <Icon name="edit outline" />
              แก้ไขรายการ
            </Button>
          </div>
        ),
      };
    });
  }, [props.SaveServiceOrderSequence?.serviceList, props.masterOptions?.productType]);

  const statusOption = useMemo(() => {
    return [
      {
        key: "ALL",
        value: "ALL",
        text: "ทั้งหมด",
      },
      ...serviceStatusOption,
    ];
  }, [serviceStatusOption]);

  const handleSelectService = (open: boolean, item?: ProductServiceSerializer) => {
    let itemData: Partial<ProductServiceSerializer>;

    if (item) {
      const divisionId = item.of_division?.id ?? null;
      const pTypeId = item.p_type?.id ?? null;
      const unitId = item.unit?.id ?? null;

      const divisionPrefix = divisionId ? `${divisionId}_` : "";

      const formattedStartDate = formatADtoBEString(item.start_date);
      const formattedEndDate = formatADtoBEString(item.end_date);

      const trimmedCode =
        divisionPrefix && item.code?.startsWith(divisionPrefix)
          ? item.code.slice(divisionPrefix.length)
          : item.code;

      itemData = {
        ...item,
        of_division: divisionId,
        p_type: pTypeId,
        unit: unitId,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        code: trimmedCode ?? "",
      };
    } else {
      itemData = {
        code: "",
        of_division: props.selectedDivision?.id ?? null,
        active_flag: "INACTIVE",
      };
    }

    setOpenRuleMod(open);
    setSelectService(itemData);
  };

  const handleSearch = (params: any = null) => {
    props.runSequence({
      sequence: "SaveServiceOrder",
      action: ACTIONS.SEARCH_SERVICE,
      filter,
      ...(params ? { ...params } : {}),
    });
  };

  const handlePageChange = useCallback(
    (activePage: number) => {
      handleSearch({ activePage });
    },
    [props.SaveServiceOrderSequence?.serviceListPage, props.runSequence]
  );

  const handleCloseSnackMessage = useCallback(() => {
    props.setProp(`errorMessage.${MAIN_CARD_NAME}`, null);
    props.setProp(`successMessage.${MAIN_CARD_NAME}`, null);
  }, [props.setProp]);

  const handleChangeData = useCallback(
    (key: string, value: string) => {
      setFilter((prev) => ({ ...prev, [key]: value }));
    },
    [filter, setFilter]
  );

  const handleCloseSettingRuleMod = () => {
    setOpenRuleMod(false);
  };

  const handleSaveSettingRule = (data: Partial<ProductServiceSerializer>) => {
    props.runSequence({
      sequence: "SaveServiceOrder",
      action: ACTIONS.CREATE_SERVICE,
      data: {
        ...data,
      },
      isEdit: selectService?.code !== "",
      callback: (open: boolean) => {
        return setOpenRuleMod(open);
      },
    });
  };

  return (
    <div style={{ margin: "10px 0px 0px 0px" }}>
      {/* --- Error & Success Message --- */}
      <SnackMessage
        error={props.errorMessage?.[MAIN_CARD_NAME]}
        success={props.successMessage?.[MAIN_CARD_NAME]}
        onClose={handleCloseSnackMessage}
      />

      {/* --- Loader --- */}
      <Dimmer active={props.loadingStatus?.[MAIN_CARD_NAME]} inverted>
        <Loader inverted>Loading</Loader>
      </Dimmer>

      {/* --- Mod Setting Rule --- */}
      <ModSettingRule
        setProp={props.setProp}
        buttonLoadCheck={props.buttonLoadCheck}
        open={openRuleMod}
        onClose={handleCloseSettingRuleMod}
        productTypeOptions={props.masterOptions?.productType}
        unitOptions={props.masterOptions?.unit}
        onSave={handleSaveSettingRule}
        selectService={selectService}
        // common
        selectedDivision={props.selectedDivision}
      />

      {/* --- Main Component --- */}
      <CardSettingServiceUX
        // data

        // filer
        serviceStatusOption={statusOption}
        code={filter?.code}
        name={filter?.name}
        activeFlag={filter?.active_flag}
        onChangeData={handleChangeData}
        // table
        serviceData={formattedServiceList}
        buttonSearch={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={BTN_ACTS.SEARCH_SERVICE}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH_SERVICE]}
            // config
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            fluid={true}
            size="small"
            title={"ค้นหาข้อมูล"}
          />
        }
        buttonAddNewService={
          <Button
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            fluid
            size="small"
            onClick={() => {
              handleSelectService(true);
            }}
          >
            <Icon name="plus" />
            เพิ่มรายการใหม่
          </Button>
        }
        pagination={
          <PaginationShorthand
            activePage={props.SaveServiceOrderSequence?.serviceListPage?.activePage || 1}
            total={props.SaveServiceOrderSequence?.serviceListPage?.total || 0}
            limit={DEFAULT_PRODUCT_LIST_LIMIT}
            onPageChange={handlePageChange}
          />
        }
      />
    </div>
  );
};

CardSettingService.displayName = "CardSettingService";
export default CardSettingService;

export const statusStyles = {
  // ** Note : 1 = เปิดขาย, 2 : = ยังไม่เปิดขาย
  ACTIVE: {
    color: "rgba(145, 197, 97, 1)",
    backgroundColor: "rgba(84, 174, 0, 0.15)",
  },
  INACTIVE: {
    color: "rgba(255, 0, 0, 1)",
    backgroundColor: "rgba(174, 0, 0, 0.15)",
  },
  "": {
    color: "rgba(255, 255, 255, 1)",
    backgroundColor: "rgb(185, 185, 185)",
  },
};
